/**
 * Accès sûr au `localStorage` : mémoire de confort (préférences, acquittements), jamais une raison
 * de casser un écran. L'accès même à `localStorage` peut lever (stockage bloqué, navigation privée),
 * une lecture peut rendre n'importe quoi, une écriture peut échouer (quota) : tout est absorbé.
 */

export type SafeStorage = Pick<Storage, 'getItem' | 'setItem'>

export const getLocalStorage = (): Storage | undefined => {
  try {
    return globalThis.localStorage
  } catch {
    return undefined
  }
}

/** Chaîne stockée sous `key`, `null` si absente ou stockage indisponible. */
export function readStorageRaw(storage: SafeStorage | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null
  } catch {
    return null
  }
}

/** Valeur JSON d'une chaîne stockée, `undefined` si absente ou illisible. */
export function parseStorageJson(raw: string | null): unknown {
  if (raw === null) return undefined
  try {
    return JSON.parse(raw) as unknown
  } catch {
    return undefined
  }
}

/** Valeur JSON stockée sous `key`, `undefined` si absente, illisible ou stockage indisponible. */
export const readStorageJson = (storage: SafeStorage | undefined, key: string): unknown =>
  parseStorageJson(readStorageRaw(storage, key))

/**
 * Écrit `value` en JSON sous `key`. `false` : rien n'est gardé (stockage indisponible, écriture
 * refusée, quota) ; l'appelant qui en dépend peut se replier et le signaler.
 */
export function writeStorageJson(
  storage: SafeStorage | undefined,
  key: string,
  value: unknown,
): boolean {
  if (!storage) return false
  try {
    storage.setItem(key, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

/**
 * Abonnements d'une mémoire gardée dans le `localStorage` et lue par `useSyncExternalStore` :
 * `notify` après chaque écriture de la page ; un autre onglet qui écrit l'une de ses clés
 * (`ownsKey`, événement `storage`, `key` nulle quand il vide tout le stockage) prévient aussi les
 * abonnés.
 */
export function createStorageSubscription(ownsKey: (key: string) => boolean) {
  const listeners = new Set<() => void>()
  const notify = () => {
    for (const listener of listeners) listener()
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || ownsKey(event.key)) notify()
  }
  // Un seul écouteur `storage` par mémoire, posé au premier abonné. Absent hors navigateur (tests
  // `node --test`).
  const subscribe = (listener: () => void): (() => void) => {
    if (listeners.size === 0) globalThis.addEventListener?.('storage', onStorage)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
      if (listeners.size === 0) globalThis.removeEventListener?.('storage', onStorage)
    }
  }
  return { subscribe, notify }
}
